/**
 * Wiring for the session revocation check — CU-86akkg3gu Part 3.
 *
 * The decision logic lives in `utils/mfa/sessionRevocationCheck.ts`, which has no
 * DOM or React dependency and carries the measured rationale. This hook is only
 * the trigger surface: WHEN to ask.
 *
 * ⚠️ IT NEVER SIGNS ANYONE OUT. On a genuine revocation the Firebase SDK fires
 * `onAuthStateChanged(null)` itself and the existing `_authenticated.tsx` guard
 * redirects. See the module header for the measurement.
 *
 * ── TRIGGERS, IN ORDER OF HOW MUCH WORK THEY DO ───────────────────────────
 *
 *  1. **focus** — the patient returned to the tab. This is the realistic case
 *     for "revoked on another device while this one sat idle", and it makes
 *     detection effectively immediate for anyone actually using the app.
 *  2. **route** — navigating onto a new authenticated surface. ⚠️ In-app
 *     navigation serves PHI from React Query CACHE and requires no new Firestore
 *     subscription (Addendum 2), so without this a revoked session could browse
 *     charts indefinitely without a single network call. This is the trigger
 *     that closes that hole.
 *  3. **backstop** — an idle foreground tab. Worst case only.
 */

import { useEffect, useRef } from 'react';
import {
  BACKSTOP_INTERVAL_MS,
  checkSessionRevocation,
} from '../utils/mfa/sessionRevocationCheck';

/**
 * ⚠️ COALESCING WINDOW. A route change often lands within milliseconds of a
 * focus event (tab back → click a link), and React may re-run effects. Without
 * this, one patient action would fire several identical network calls.
 *
 * Deliberately SHORTER than any trigger's natural cadence, so it suppresses
 * duplicates without ever suppressing a real check.
 */
const MIN_INTERVAL_MS = 10_000;

/**
 * ⚠️ MODULE SCOPE, NOT A REF — AND THIS IS THE WHOLE POINT.
 *
 * The first version held this in `useRef(0)`. That looks equivalent and is not:
 * a ref belongs to a COMPONENT INSTANCE, so every re-mount of the host
 * component gets a fresh `0`, `now - 0` always clears the window, and the
 * throttle coalesces nothing.
 *
 * `WithHeaderChrome` re-mounts readily — the persona guards and the sign-out
 * suppression above it re-render and remount this subtree — so in practice the
 * guard reset constantly.
 *
 * ⚠️ MEASURED ON STAGING, 2026-09-23: ~20 requests to
 * `securetoken.googleapis.com` in roughly ten seconds, against a design budget
 * of one per ten seconds. Production, running the previous build, held at 6
 * requests total. **The operator caught this; the unit tests did not, because
 * they render the hook once and never re-mount it.**
 *
 * At module scope the throttle is per-TAB, which is the correct scope: the
 * question "has this session been revoked" is a property of the session, not of
 * whichever component happens to be mounted.
 */
let lastCheckedAt = 0;

/** Exported for tests — a module-scoped throttle would otherwise leak between them. */
export function __resetRevocationThrottle(): void {
  lastCheckedAt = 0;
}

function runCheck(trigger: string): void {
  const now = Date.now();
  if (now - lastCheckedAt < MIN_INTERVAL_MS) return;
  lastCheckedAt = now;
  void checkSessionRevocation(trigger);
}

/**
 * @param pathname current route. A change fires a `route` check.
 * @param enabled false while no patient is signed in, so the hook can sit above
 *   an early return without asking about a session that does not exist.
 */
export function useSessionRevocationCheck(pathname: string, enabled: boolean): void {
  const run = useRef(runCheck);

  // ── focus + backstop ────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    run.current('startup');

    const onFocus = () => run.current('focus');
    // `visibilitychange` catches the mobile-browser case where a tab becomes
    // visible without the window receiving focus.
    const onVisible = () => {
      if (document.visibilityState === 'visible') run.current('focus');
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const id = window.setInterval(() => run.current('backstop'), BACKSTOP_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(id);
    };
  }, [enabled]);

  // ── route changes ───────────────────────────────────────────────────────
  //
  // ⚠️ Separate effect, keyed on `pathname`. Folding this into the effect above
  // would tear down and rebuild the interval and both listeners on every
  // navigation — and would reset the backstop, so a patient clicking around
  // steadily would never reach it.
  useEffect(() => {
    if (!enabled) return;
    run.current('route');
  }, [pathname, enabled]);
}
